import { type FC, type ReactNode, useMemo } from "react";

import { ChartTooltipContent, type ChartConfig } from "@/components/flexkit/charts";
import { BaseAreaChart } from "@/components/flexkit/charts/BaseAreaChart";
import { chartColors } from "@/components/flexkit/charts/chart-colors";
import { formatCompactNumber, formatChartAxisDate } from "@/components/flexkit/charts/chart-formatters";
import { ChartLoadingState } from "@/components/flexkit/charts/ChartLoadingState";
import { Text } from "@/components/flexkit/typography/Text";
import { Card } from "@/components/podkit/Card";
import { OCUs } from "@/routes/settings/organization/billing/OCU";

type ForecastDataPoint = {
    date: string;
    remaining: number;
};

type Props = {
    availableCredits: number;
    avgDailyBurn: number;
    /** Optional action element rendered at the top-right of the card header. */
    headerAction?: ReactNode;
    loading?: boolean;
};

const FORECAST_COLOR = chartColors.chart3;

function formatFutureDate(daysFromNow: number): string {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return formatChartAxisDate(d);
}

export const ForecastChart: FC<Props> = ({ availableCredits, avgDailyBurn, headerAction, loading }) => {
    const forecastData = useMemo((): ForecastDataPoint[] => {
        if (avgDailyBurn <= 0) {
            // No burn rate — just show a flat line at current credits for 30 days
            return [
                { date: formatFutureDate(0), remaining: availableCredits },
                { date: formatFutureDate(30), remaining: availableCredits },
            ];
        }

        const maxDays = 100;
        const points: ForecastDataPoint[] = [];
        let remaining = availableCredits;
        let day = 0;

        while (remaining > 0 && day < maxDays) {
            points.push({ date: formatFutureDate(day), remaining: Math.round(remaining) });
            remaining -= avgDailyBurn;
            day++;
        }

        // Final point at 0 (or projected remaining if capped)
        const finalRemaining = remaining > 0 ? Math.round(remaining) : 0;
        points.push({ date: formatFutureDate(day), remaining: finalRemaining });

        return points;
    }, [availableCredits, avgDailyBurn]);

    const daysUntilZero = avgDailyBurn > 0 ? Math.ceil(availableCredits / avgDailyBurn) : undefined;

    const chartConfig = {
        remaining: {
            label: "Remaining credits",
            color: FORECAST_COLOR,
        },
    } satisfies ChartConfig;

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <Text className="text-lg font-semibold text-content-primary">Credit Forecast</Text>
                    <Text className="text-sm text-content-secondary">
                        {daysUntilZero !== undefined ? (
                            <>
                                {"At current usage ("}
                                {Math.round(avgDailyBurn).toLocaleString()} <OCUs />
                                {"/day), credits will "}
                                run out in {daysUntilZero} {daysUntilZero === 1 ? "day" : "days"}
                            </>
                        ) : (
                            "No usage detected — credits will not deplete"
                        )}
                    </Text>
                </div>
                {headerAction}
            </div>

            {loading ? (
                <ChartLoadingState />
            ) : (
                <BaseAreaChart
                    data={forecastData}
                    config={chartConfig}
                    height="sm"
                    yAxisFormatter={formatCompactNumber}
                    tooltipContent={
                        <ChartTooltipContent
                            valueFormatter={(value) => `${Math.round(Number(value)).toLocaleString()} OCUs`}
                        />
                    }
                    series={[{ dataKey: "remaining", color: FORECAST_COLOR }]}
                />
            )}
        </Card>
    );
};
